import React, { useEffect, useState, useMemo } from "react";
import { Modal, Form, Input, DatePicker, Select, Button, message, Space, Spin, Row, Col, Switch, Divider, Card, Typography, Tag } from "antd";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import { Method_POST_CONTEXTO } from "../../../../service/api";
import { DeleteOutlined, PlusOutlined, SwapOutlined, UserOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

const { Option } = Select;
const { Text, Title: TypographyTitle } = Typography;
const { CheckableTag } = Tag;

export default function EditLancamento({
    visible,
    lancamentoId,
    competencia,
    contasContabeis: contasProp,
    onSaved,
    onClose,
    title,
    zIndex,
    onFocus,
    modalKey,
    getContainer = () => document.body
}) {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        visible,
        top: 20,
        modalKey: modalKey || 'editLancamento',
        initialWidth: 1000,
        initialHeight: 'auto',
        overflow: 'visible'
    });
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [contas, setContas] = useState(contasProp || []);
    const [isMultiplo, setIsMultiplo] = useState(false);
    const [replicate, setReplicate] = useState(false);
    const [auditInfo, setAuditInfo] = useState(null);

    const dataReferencia = Form.useWatch("dataReferencia", form);
    const mesesReplicar = Form.useWatch("mesesReplicar", form) || [];

    const mode = lancamentoId ? "edit" : "new";

    const mesesOptions = [
        { label: 'Janeiro', value: 1 }, { label: 'Fevereiro', value: 2 }, { label: 'Março', value: 3 },
        { label: 'Abril', value: 4 }, { label: 'Maio', value: 5 }, { label: 'Junho', value: 6 },
        { label: 'Julho', value: 7 }, { label: 'Agosto', value: 8 }, { label: 'Setembro', value: 9 },
        { label: 'Outubro', value: 10 }, { label: 'Novembro', value: 11 }, { label: 'Dezembro', value: 12 },
    ];

    const parseToDouble = (value) => {
        if (!value) return 0;
        if (typeof value === 'number') return value;
        const cleanValue = String(value).replace("R$ ", "").replace(/\./g, "").replace(",", ".");
        return parseFloat(cleanValue) || 0;
    };

    const formatCurrency = (val) => Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

    // --- CARREGAMENTO DE DADOS ---
    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setIsMultiplo(false);
            setReplicate(false);
            setAuditInfo(null);
            return;
        }

        if (lancamentoId) {
            setLoading(true);
            Method_POST_CONTEXTO("/empresa/contabilidade/lancamento/consultar", { id: lancamentoId })
                .then((data) => {
                    const partidasBackend = data.partidas || [];
                    const ehMultiplo = partidasBackend.length > 2;
                    setIsMultiplo(ehMultiplo);

                    const partidasFormatadas = partidasBackend.map(p => ({
                        tipo: p.tipo,
                        contaId: p.contaContabil?.id,
                        valor: formatCurrency(p.valor)
                    }));

                    const debito = partidasBackend.find(p => p.tipo === 'D');
                    const credito = partidasBackend.find(p => p.tipo === 'C');

                    form.setFieldsValue({
                        id: data.id,
                        dataReferencia: data.dataReferencia ? dayjs(Number(data.dataReferencia)) : null,
                        nome: data.nome || "",
                        idGrupo: data.idGrupo,
                        valorSimples: debito ? formatCurrency(debito.valor) : (credito ? formatCurrency(credito.valor) : "0,00"),
                        contaDebitoId: debito?.contaContabil?.id,
                        contaCreditoId: credito?.contaContabil?.id,
                        partidas: partidasFormatadas
                    });

                    // Armazena informações de auditoria
                    setAuditInfo({
                        usuarioCadastro: data.usuarioCadastro?.id || data.usuarioCadastro?.nome || null,
                        dataCadastro: data.dataCadastro ? dayjs(Number(data.dataCadastro)) : null,
                        usuarioAlteracao: data.usuarioAlteracao?.id || data.usuarioAlteracao?.nome || null,
                        dataAlteracao: data.dataAlteracao ? dayjs(Number(data.dataAlteracao)) : null,
                    });
                })
                .finally(() => setLoading(false));
        } else {
            const dt = competencia ? dayjs(competencia.split('/').reverse().join('-')) : dayjs();
            form.setFieldsValue({
                dataReferencia: dt.isValid() ? dt : dayjs(),
                partidas: [{ tipo: 'D' }, { tipo: 'C' }]
            });
        }
    }, [visible, lancamentoId, form, competencia]);

    // Busca de contas contábeis
    useEffect(() => {
        if (visible && (!contasProp || contasProp.length === 0)) {
            Method_POST_CONTEXTO("/empresa/contabilidade/conta-contabil/listar")
                .then((res) => setContas(Array.isArray(res) ? res : res?.data ?? []))
                .catch(() => message.error("Erro ao carregar contas contábeis"));
        } else if (contasProp) {
            setContas(contasProp);
        }
    }, [visible, contasProp]);

    const contasOptions = useMemo(() => {
        return (Array.isArray(contas) ? contas : [])
            .filter((c) => c.lancamento !== false)
            .map((c) => ({ id: c.id, label: `${c.codigo} - ${c.nome}` }));
    }, [contas]);

    const checkBalance = (partidas = []) => {
        const totalDebito = partidas.filter(p => p?.tipo === 'D').reduce((acc, curr) => acc + parseToDouble(curr?.valor), 0);
        const totalCredito = partidas.filter(p => p?.tipo === 'C').reduce((acc, curr) => acc + parseToDouble(curr?.valor), 0);
        const diff = Math.abs(totalDebito - totalCredito);
        return { totalDebito, totalCredito, diff, isBalanced: totalDebito > 0 && totalCredito > 0 && diff < 0.01 };
    };

    const handleSwitchMultiplo = (checked) => {
        if (checked) {
            const values = form.getFieldsValue();
            form.setFieldValue('partidas', [
                { tipo: 'D', contaId: values.contaDebitoId, valor: values.valorSimples },
                { tipo: 'C', contaId: values.contaCreditoId, valor: values.valorSimples }
            ]);
        }
        setIsMultiplo(checked);
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const dataRef = values.dataReferencia;
            let partidasFormatadas = [];

            if (!isMultiplo) {
                partidasFormatadas = [
                    { tipo: "D", contaContabil: { id: values.contaDebitoId }, valor: parseToDouble(values.valorSimples) },
                    { tipo: "C", contaContabil: { id: values.contaCreditoId }, valor: parseToDouble(values.valorSimples) }
                ];
            } else {
                partidasFormatadas = values.partidas.map(p => ({
                    tipo: p.tipo,
                    contaContabil: { id: p.contaId },
                    valor: parseToDouble(p.valor)
                }));
            }

            const payload = {
                id: lancamentoId || undefined,
                dataReferencia: dataRef.valueOf(),
                nome: values.nome,
                idGrupo: values.idGrupo,
                partidas: partidasFormatadas,
                ano: dataRef.year(),
                mes: dataRef.month() + 1
            };

            const url = lancamentoId ? "/empresa/contabilidade/lancamento/atualizar" : "/empresa/contabilidade/lancamento/cadastrar";

            if (!lancamentoId && replicate && values.mesesReplicar?.length > 0) {
                // Primeiro, salvamos o lançamento original
                await Method_POST_CONTEXTO(url, payload);

                // Em seguida, criamos as replicações para os outros meses
                const payloads = values.mesesReplicar.map(mesAlvo => {
                    const novaData = dataRef.month(mesAlvo - 1);
                    return {
                        ...payload,
                        dataReferencia: novaData.valueOf(),
                        mes: mesAlvo,
                        ano: novaData.year()
                    };
                });

                await Promise.all(payloads.map(p => Method_POST_CONTEXTO(url, p)));
                message.success(`Lançamento original e mais ${payloads.length} replicações salvos com sucesso!`);
            } else {
                // Lançamento normal (edição ou novo sem replicação)
                await Method_POST_CONTEXTO(url, payload);
                message.success("Lançamento salvo com sucesso!");
            }
            
            // Notifica o componente pai e fecha o modal
            onSaved();
        } catch (err) {
            message.error(err.message || "Erro ao salvar");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={title || <Space><SwapOutlined /><span>{mode === "edit" ? "Editar Lançamento" : "Novo Lançamento"}</span></Space>}
            open={visible}
            onCancel={onClose}
            width={1000}
            footer={null}
            getContainer={false}
            {...draggableProps}
            zIndex={zIndex || 15000}
            styles={{
                ...draggableProps.styles,
                body: {
                    maxHeight: "80vh",
                    padding: '12px',
                    overflowY: "auto"
                }
            }}
        >
            <Spin spinning={loading}>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Card size="small" style={{ background: '#f8f9fa', marginBottom: 20 }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label={<Text strong>Data</Text>} name="dataReferencia" rules={[{ required: true }]}>
                                    <DatePicker format={["DD/MM/YYYY", "DDMMYYYY"]} style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <Form.Item label="Descrição Principal" name="nome" rules={[{ required: true }]}>
                                    <Input placeholder="Ex: Pagamento Mensal" />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item label="Múltiplo">
                                    <Switch checked={isMultiplo} onChange={handleSwitchMultiplo} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item label="Replicar">
                                    <Switch disabled={mode === "edit"} checked={replicate} onChange={setReplicate} />
                                </Form.Item>
                            </Col>
                        </Row>
                        {replicate && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong>Meses para Replicar</Text>
                                    <Space size={4}>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (!dataReferencia) return;
                                                const currentMonth = dataReferencia.month() + 1;
                                                const nextThree = [];
                                                for (let i = 1; i <= 3; i++) {
                                                    const m = ((currentMonth + i - 1) % 12) + 1;
                                                    nextThree.push(m);
                                                }
                                                form.setFieldValue("mesesReplicar", nextThree);
                                            }}
                                        >
                                            +3 Meses
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (!dataReferencia) return;
                                                const currentMonth = dataReferencia.month() + 1;
                                                const nextSix = [];
                                                for (let i = 1; i <= 6; i++) {
                                                    const m = ((currentMonth + i - 1) % 12) + 1;
                                                    nextSix.push(m);
                                                }
                                                form.setFieldValue("mesesReplicar", nextSix);
                                            }}
                                        >
                                            +6 Meses
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (!dataReferencia) return;
                                                const currentMonth = dataReferencia.month() + 1;
                                                const restOfYear = [];
                                                for (let m = currentMonth + 1; m <= 12; m++) {
                                                    restOfYear.push(m);
                                                }
                                                form.setFieldValue("mesesReplicar", restOfYear);
                                            }}
                                        >
                                            Resto do Ano
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            onClick={() => form.setFieldValue("mesesReplicar", [])}
                                        >
                                            Limpar
                                        </Button>
                                    </Space>
                                </div>

                                <Form.Item name="mesesReplicar" rules={[{ required: true, message: "Selecione ao menos um mês" }]}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(6, 1fr)',
                                        gap: '8px',
                                        background: '#fff',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #d9d9d9'
                                    }}>
                                        {mesesOptions.map(m => {
                                            const isCurrentMonth = dataReferencia && (dataReferencia.month() + 1 === m.value);
                                            const isSelected = mesesReplicar.includes(m.value);

                                            return (
                                                <CheckableTag
                                                    key={m.value}
                                                    checked={isSelected}
                                                    disabled={isCurrentMonth}
                                                    onChange={(checked) => {
                                                        if (isCurrentMonth) return;
                                                        const nextSelected = checked
                                                            ? [...mesesReplicar, m.value]
                                                            : mesesReplicar.filter(v => v !== m.value);
                                                        form.setFieldValue("mesesReplicar", nextSelected);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        padding: '4px 0',
                                                        margin: 0,
                                                        border: '1px solid transparent',
                                                        borderRadius: '4px',
                                                        cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
                                                        backgroundColor: isSelected ? '#1677ff' : (isCurrentMonth ? '#f5f5f5' : '#fafafa'),
                                                        color: isSelected ? '#fff' : (isCurrentMonth ? '#bfbfbf' : 'rgba(0, 0, 0, 0.88)'),
                                                        fontWeight: isCurrentMonth ? 700 : 400
                                                    }}
                                                >
                                                    {m.label.substring(0, 3)}
                                                    {isCurrentMonth && <div style={{ fontSize: '8px', lineHeight: 1 }}>Atual</div>}
                                                </CheckableTag>
                                            );
                                        })}
                                    </div>
                                </Form.Item>
                                {mesesReplicar.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Serão criados <strong>{mesesReplicar.length}</strong> lançamentos adicionais.
                                    </Text>
                                )}
                            </div>
                        )}
                    </Card>

                    {/* Informações de Auditoria - apenas no modo edição */}
                    {mode === "edit" && auditInfo && (
                        <Card
                            size="small"
                            style={{
                                background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%)',
                                marginBottom: 20,
                                border: '1px solid #d6e4ff'
                            }}
                        >
                            <Row gutter={16} align="middle">
                                {auditInfo.usuarioCadastro && (
                                    <Col span={12}>
                                        <Space size={8} align="center">
                                            <UserOutlined style={{ color: '#1677ff', fontSize: 16 }} />
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.2 }}>Cadastrado por</Text>
                                                <Text strong style={{ fontSize: 13 }}>{auditInfo.usuarioCadastro}</Text>
                                            </div>
                                        </Space>
                                    </Col>
                                )}
                                {auditInfo.dataCadastro && (
                                    <Col span={12}>
                                        <Space size={8} align="center">
                                            <ClockCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.2 }}>Data do cadastro</Text>
                                                <Text strong style={{ fontSize: 13 }}>{auditInfo.dataCadastro.format('DD/MM/YYYY [às] HH:mm:ss')}</Text>
                                            </div>
                                        </Space>
                                    </Col>
                                )}
                                {auditInfo.usuarioAlteracao && (
                                    <Col span={12} style={{ marginTop: auditInfo.usuarioCadastro ? 8 : 0 }}>
                                        <Space size={8} align="center">
                                            <UserOutlined style={{ color: '#faad14', fontSize: 16 }} />
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.2 }}>Alterado por</Text>
                                                <Text strong style={{ fontSize: 13 }}>{auditInfo.usuarioAlteracao}</Text>
                                            </div>
                                        </Space>
                                    </Col>
                                )}
                                {auditInfo.dataAlteracao && (
                                    <Col span={12} style={{ marginTop: auditInfo.dataCadastro ? 8 : 0 }}>
                                        <Space size={8} align="center">
                                            <ClockCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.2 }}>Data da alteração</Text>
                                                <Text strong style={{ fontSize: 13 }}>{auditInfo.dataAlteracao.format('DD/MM/YYYY [às] HH:mm:ss')}</Text>
                                            </div>
                                        </Space>
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    )}

                    {!isMultiplo ? (
                        <Card title="Partida Simples">
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="Valor" name="valorSimples" rules={[{ required: true }]}>
                                        <NumericFormat customInput={Input} thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Conta Débito" name="contaDebitoId" rules={[{ required: true }]}>
                                        <Select showSearch optionFilterProp="children">
                                            {contasOptions.map(c => <Option key={c.id} value={c.id}>{c.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Conta Crédito" name="contaCreditoId" rules={[{ required: true }]}>
                                        <Select showSearch optionFilterProp="children">
                                            {contasOptions.map(c => <Option key={c.id} value={c.id}>{c.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    ) : (
                        <>
                            <Form.List name="partidas">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Row gutter={8} key={key} align="bottom" style={{ marginBottom: 8 }}>
                                                <Col span={4}>
                                                    <Form.Item {...restField} name={[name, 'tipo']} rules={[{ required: true }]}>
                                                        <Select placeholder="D/C"><Option value="D">Débito</Option><Option value="C">Crédito</Option></Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={10}>
                                                    <Form.Item {...restField} name={[name, 'contaId']} rules={[{ required: true }]}>
                                                        <Select showSearch optionFilterProp="children">
                                                            {contasOptions.map(c => <Option key={c.id} value={c.id}>{c.label}</Option>)}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={7}>
                                                    <Form.Item {...restField} name={[name, 'valor']} rules={[{ required: true }]}>
                                                        <NumericFormat
                                                            customInput={Input}
                                                            thousandSeparator="."
                                                            decimalSeparator=","
                                                            prefix="R$ "
                                                            decimalScale={2}
                                                            fixedDecimalScale
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={3}>
                                                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} disabled={fields.length <= 2} />
                                                </Col>
                                            </Row>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Adicionar Conta</Button>
                                    </>
                                )}
                            </Form.List>

                            <Form.Item shouldUpdate={(prev, curr) => prev.partidas !== curr.partidas} style={{ marginTop: 20 }}>
                                {() => {
                                    const { totalDebito, totalCredito, diff, isBalanced } = checkBalance(form.getFieldValue('partidas'));
                                    return (
                                        <Card size="small" style={{ background: isBalanced ? '#f6ffed' : '#fff1f0' }}>
                                            <Row textAlign="center">
                                                <Col span={8}>Total D: <b>R$ {formatCurrency(totalDebito)}</b></Col>
                                                <Col span={8}>Total C: <b>R$ {formatCurrency(totalCredito)}</b></Col>
                                                <Col span={8}>Diferença: <b style={{ color: isBalanced ? 'green' : 'red' }}>R$ {formatCurrency(diff)}</b></Col>
                                            </Row>
                                        </Card>
                                    );
                                }}
                            </Form.Item>
                        </>
                    )}

                    <Divider />
                    <Row justify="end" gutter={10}>
                        <Col><Button onClick={onClose}>Cancelar</Button></Col>
                        <Col>
                            <Form.Item shouldUpdate noStyle>
                                {() => {
                                    const { isBalanced } = checkBalance(form.getFieldValue('partidas'));
                                    return (
                                        <Button type="primary" htmlType="submit" loading={loading} disabled={isMultiplo && !isBalanced}>
                                            Salvar Lançamento
                                        </Button>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
}